import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "react-query";
import { fetchApi } from "../../helpers/reactQueryApi";
import { map } from "lodash";
import { File, Folder, SubFolder } from "@/@types/files.ts";

export const useFetchFile = (fileId: string | undefined) => {
  return useQuery<File>(`/api/storage/files/${fileId}`, {
    enabled: !!fileId,
  });
};

// Lazy permission lookup against file_server for a single file. Returns a
// useMutation; call `mutate()` (or await `mutateAsync()`) to fire the request.
// Resolves to a per-action booleans hash, e.g. { lock: true, finalize: false }.
// Actions absent from the response come back undefined; treat absence as deny.
export function useCheckFilePermissions(fileId: string, actions: string[]) {
  return useMutation<Record<string, boolean>>(async () => {
    const params = new URLSearchParams();
    actions.forEach((action) => params.append("actions[]", action));
    const response = await fetchApi(
      `/api/storage/files/${fileId}/permissions?${params}`,
    );
    return response.permissions ?? {};
  });
}

export function useLockFile() {
  const queryClient = useQueryClient();
  return useMutation<File, Error, { fileId: string }>(
    ({ fileId }) =>
      fetchApi(`/api/storage/files/${fileId}/lock`, {
        method: "PUT",
        body: {
          locked_until: new Date(
            Date.now() +
              (Number(
                Preload.current_network.config.files
                  ?.default_lock_period_in_days,
              ) || 7) *
                24 *
                60 *
                60 *
                1000,
          ).toISOString(),
        },
      }),
    {
      onSuccess: (_data, { fileId }) => {
        queryClient.invalidateQueries(`/api/storage/files/${fileId}`);
      },
    },
  );
}

export function useUnlockFile() {
  const queryClient = useQueryClient();
  return useMutation<File, Error, { fileId: string }>(
    ({ fileId }) =>
      fetchApi(`/api/storage/files/${fileId}/lock`, {
        method: "DELETE",
      }),
    {
      onSuccess: (_data, { fileId }) => {
        queryClient.invalidateQueries(`/api/storage/files/${fileId}`);
      },
    },
  );
}

export function useFinalizeFile() {
  const queryClient = useQueryClient();
  return useMutation<File, Error, { fileId: string }>(
    ({ fileId }) =>
      fetchApi(`/api/storage/files/${fileId}/finalize`, {
        method: "PUT",
      }),
    {
      onSuccess: (_data, { fileId }) => {
        queryClient.invalidateQueries(`/api/storage/files/${fileId}`);
        // the approval counters skip finalized files
        queryClient.invalidateQueries(["unreads"]);
      },
    },
  );
}

export function useUnfinalizeFile() {
  const queryClient = useQueryClient();
  return useMutation<File, Error, { fileId: string }>(
    ({ fileId }) =>
      fetchApi(`/api/storage/files/${fileId}/finalize`, {
        method: "DELETE",
      }),
    {
      onSuccess: (_data, { fileId }) => {
        queryClient.invalidateQueries(`/api/storage/files/${fileId}`);
        // the approval counters skip finalized files
        queryClient.invalidateQueries(["unreads"]);
      },
    },
  );
}

export function useDeleteVersion() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { fileId: string; versionId: string }>(
    ({ fileId, versionId }) =>
      fetchApi(`/api/storage/files/${fileId}/versions/${versionId}`, {
        method: "DELETE",
      }),
    {
      onSuccess: (_data, { fileId }) => {
        queryClient.invalidateQueries(`/api/storage/files/${fileId}/versions`);
        queryClient.invalidateQueries(`/api/storage/files/${fileId}`);
      },
    },
  );
}

type FolderPage = { data: Folder; nextPageUrl?: string };

function addNew<T extends { id: string }>(
  target: Map<string, T>,
  items: T[] = [],
) {
  const fresh = items.filter((item) => !target.has(item.id));
  fresh.forEach((item) => target.set(item.id, item));
  return fresh.length;
}

// Safety net against a `next` link that never ends
export const MAX_FOLDER_PAGES = 100;

// file_server pages folder contents (`Link: rel="next"`), so load every page
export async function fetchFolderWithAllContents(
  url: string,
  signal?: AbortSignal,
) {
  const first: FolderPage = await fetchApi(url, {
    parseLinkHeaders: true,
    signal,
  });
  const subfolders = new Map<string, SubFolder>();
  const files = new Map<string, File>();
  const addPage = ({ data }: FolderPage) =>
    addNew(subfolders, data.subfolders) + addNew(files, data.files) > 0;

  addPage(first);
  let next = first.nextPageUrl;
  for (let pages = 1; next && pages < MAX_FOLDER_PAGES; pages++) {
    // the link names the network's primary host, which may not be this one
    const { pathname, search } = new URL(next, window.location.href);
    const page: FolderPage = await fetchApi(pathname + search, {
      parseLinkHeaders: true,
      signal,
    });
    // root folders repeat their group and stream folders on every page
    if (!addPage(page)) break;
    next = page.nextPageUrl;
  }

  return {
    ...first.data,
    subfolders: [...subfolders.values()],
    files: [...files.values()],
  };
}

// Array keys, so invalidating "/api/storage/folders" reaches these queries
export const useFetchFolder = (
  id: string | undefined,
  namespace = "folders",
) => {
  return useQuery<Folder, Error>(
    [`/api/storage/${namespace}`, id],
    // react-query aborts the signal once nobody uses the query any more, so
    // navigating away stops loading further pages
    ({ signal }) =>
      fetchFolderWithAllContents(
        `/api/storage/${namespace}/${id}?limit=1000`,
        signal,
      ),
    {
      enabled: !!id,
    },
  );
};

export const useFetchFolderMetadata = (
  id: string | undefined,
  namespace = "folders",
) => {
  return useQuery<Folder, Error>(
    [`/api/storage/${namespace}`, id, "metadata"],
    () => fetchApi(`/api/storage/${namespace}/${id}?limit=1`),
    {
      enabled: !!id,
    },
  );
};

type CreateFolderArgs = {
  name: string;
  description?: string;
  parent_folder_id?: string;
  folder_profile?: Record<string, unknown>;
};

export function useCreateFolder(
  options?: UseMutationOptions<Folder, Error, CreateFolderArgs>,
) {
  const queryClient = useQueryClient();
  return useMutation<Folder, Error, CreateFolderArgs>(
    (body) =>
      fetchApi("/api/storage/folders", {
        method: "POST",
        body,
      }),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries("/api/storage/folders");
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

type CreateDocumentArgs = {
  document_type: string;
  name: string;
};

// Creates a file from a blank tixxtDOCS template (tixxt/core#258). The file
// server keeps the templates and creates the file the same way it creates an
// uploaded one, so permissions, the extension filter and the virus scan apply
// unchanged. What comes back is a volatile file, published afterwards like any
// upload (useCreateFiles).
export function useCreateDocument(
  options?: UseMutationOptions<{ id: string }, Error, CreateDocumentArgs>,
) {
  return useMutation<{ id: string }, Error, CreateDocumentArgs>(
    (body) =>
      fetchApi("/api/storage/v2/files/blank", {
        method: "POST",
        body: { ...body, locale: I18n.currentLocale() },
      }),
    options,
  );
}

type UpdateFolderArgs = {
  folderId: string;
  name: string;
  description?: string;
  folder_profile?: Record<string, unknown>;
};

export function useUpdateFolder(
  options?: UseMutationOptions<Folder, Error, UpdateFolderArgs>,
) {
  const queryClient = useQueryClient();
  return useMutation<Folder, Error, UpdateFolderArgs>(
    ({ folderId, ...body }) =>
      fetchApi(`/api/storage/folders/${folderId}`, {
        method: "PUT",
        body,
      }),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries([
          "/api/storage/folders",
          variables.folderId,
        ]);
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

type UpdateFileArgs = {
  fileId: string;
  name?: string;
  description?: string;
  folder_ids?: string[];
  file_profile?: Record<string, unknown>;
  partial_anonymous?: boolean;
};

export function useUpdateFile(
  options?: UseMutationOptions<File, Error, UpdateFileArgs>,
) {
  const queryClient = useQueryClient();
  return useMutation<File, Error, UpdateFileArgs>(
    ({ fileId, ...body }) =>
      fetchApi(`/api/storage/files/${fileId}`, {
        method: "PUT",
        body,
      }),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(`/api/storage/files/${variables.fileId}`);
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

type PublishFileVersionArgs = {
  fileId: string;
  uploadedFileId: string;
  version_description?: string;
  post_to_stream?: boolean;
};

export function usePublishFileVersion(
  options?: UseMutationOptions<unknown, Error, PublishFileVersionArgs>,
) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, PublishFileVersionArgs>(
    ({ fileId, uploadedFileId, version_description, post_to_stream }) =>
      fetchApi(`/api/storage/files/${fileId}/versions/publish`, {
        method: "POST",
        body: {
          file_id: uploadedFileId,
          version_description,
          post_to_stream,
        },
      }),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(`/api/storage/files/${variables.fileId}`);
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

type CreateFilesArgs = {
  files: { id: string }[];
  folder_ids?: string[];
  description?: string;
  post_to_stream?: boolean;
  file_profile?: Record<string, unknown>;
};

// Publishes freshly uploaded (volatile) files into the given folders, i.e. the
// submit step of the "add files" form. Replaces the old redux `publishFiles`
// action + upload saga.
export function useCreateFiles(
  options?: UseMutationOptions<unknown, Error, CreateFilesArgs>,
) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, CreateFilesArgs>(
    (body) =>
      fetchApi("/api/storage/files/publish", {
        method: "POST",
        body,
      }),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries("/api/storage/folders");
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

type useRemoveFilesAndFolders = {
  type: "files" | "folders";
  body: { [key: string]: string[] };
}[];

export function useRemoveFilesAndFolders(
  options?: UseMutationOptions<unknown, Error, useRemoveFilesAndFolders>,
) {
  return useMutation<unknown, Error, useRemoveFilesAndFolders>(
    (requests) =>
      Promise.all(
        map(requests, ({ type, body }) =>
          fetchApi(`/api/storage/${type}`, { method: "DELETE", body }),
        ),
      ),
    options,
  );
}

type useMoveFilesAndFoldersArgs = {
  targetFolderId: string;
  requests: {
    type: "files" | "folders";
    body: {
      [key: string]: string | string[];
    };
  }[];
};

export function useMoveFilesAndFolders(
  options?: UseMutationOptions<unknown, Error, useMoveFilesAndFoldersArgs>,
) {
  return useMutation<unknown, Error, useMoveFilesAndFoldersArgs>(
    ({ requests }) =>
      Promise.all(
        map(requests, ({ type, body }) =>
          fetchApi(`/api/storage/${type}/move`, { method: "POST", body }),
        ),
      ),
    options,
  );
}
